import React from "react";
import {
  Box,
  Typography,
  styled,
  Autocomplete,
  InputBase,
  StackProps,
  Stack,
  IconButton,
  Button,
  ClickAwayListener,
  Chip,
} from "@mui/material";
import { AdvancedQueryTagProps } from "../AdvancedQueryTag/advanced-query-tag";
import AdvancedQueries, { AdvancedQueriesProps } from "../AdvancedQueries/advanced-queries";
import CloseIcon from "@mui/icons-material/Close";
import { ModalContext } from "../../Modals";
import ModalSCreateAdvancedQuery, {
  ModalAdvancedQueryElementProps,
} from "../../Modals/ModalCreateAdvancedQuery/modal-create-advanced-query";
import { DbSearchAdvancedQueryInfos } from "../../../Core/Api/Types/db-search-advanced-query-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import {
  convertAdvancedQueryToString,
  convertStringToAdvancedQuery,
} from "../../../Pages/admin/Inventory/InventoryIndex/InventoryAdvancedQueries/inventory-advanced-queries";
import { cacheService } from "../../../Core/Api/services";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import SearchIcon from "@mui/icons-material/Search";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";

export interface AdvancedQueryBuilderProps extends StackProps {
  onChangeFocus: (focus: boolean) => void;
  onClickSearch: (query: string, layer: ObjectLayer, title: string) => void;
  advancedQueries: DbSearchAdvancedQueryInfos[];
  textSearch: string;
  textQuery?: string;
  addAdvancedQuery: React.Dispatch<React.SetStateAction<DbSearchAdvancedQueryInfos[]>>;
  hideButtons?: boolean;
  onQueryChange?: (newQuery: string) => void;
}

interface OptionType {
  title: { value?: string | null; label?: string | null };
  type: string;
  color?: AdvancedQueryTagProps["color"];
  values?: { value?: string | null; label?: string | null }[];
  layer?: ObjectLayer;
  tagName?: string;
  baseType?: BaseMetadataType;
}

const AdvancedQueryBuilder: React.FC<AdvancedQueryBuilderProps> = (props) => {
  const { t } = useTranslation();
  const $autocompletRef = React.useRef<HTMLDivElement>(undefined);
  const {
    onChangeFocus,
    onClickSearch,
    advancedQueries,
    addAdvancedQuery,
    textSearch,
    onQueryChange,
    hideButtons,
    ...otherProps
  } = props;

  const [advancedQuery, setAdvancedQuery] = React.useState<AdvancedQueriesProps["queries"]>([]);

  const [autocompleteHasSearchFor, setAutocompleteHasSearchFor] = React.useState(false);
  const [autocompleteQueryType, setAutocompleteQueryType] = React.useState("searchFor");
  const [autocompleteValue, setAutocompleteValue] = React.useState<OptionType[]>([]);
  const [autocompleteOpen, setAutocompleteOpen] = React.useState<boolean>(false);
  const [entityFilter, setEntityFilter] = React.useState<string>("");
  const [valuesFilter, setValuesFilter] = React.useState<string>("");
  const [searchForFilter, setSearchForFilter] = React.useState<string>("");
  const [wasEntity, setWasEntity] = React.useState<boolean>(false);
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const [layer, setLayer] = React.useState<ObjectLayer>();
  const [options, setOptions] = React.useState<OptionType[]>(
    [
      { title: { value: "Asset", label: undefined }, type: "searchFor" },
      { title: { value: "Title", label: undefined }, type: "searchFor" },
      { title: { value: "Content", label: undefined }, type: "searchFor" },
      { title: { value: "IS", label: undefined }, type: "condition" },
      { title: { value: "=", label: undefined }, type: "condition", color: "success" } as OptionType,
      { title: { value: "'IS-NOT'", label: undefined }, type: "condition", color: "error" } as OptionType,
      { title: { value: "!=", label: undefined }, type: "condition", color: "error" } as OptionType,
      { title: { value: "IN", label: undefined }, type: "condition", color: "success" } as OptionType,
      { title: { value: "'NOT-IN'", label: undefined }, type: "condition", color: "error" } as OptionType,
      { title: { value: "<", label: undefined }, type: "condition", color: "error", tagName: "date" } as OptionType,
      { title: { value: ">", label: undefined }, type: "condition", color: "success", tagName: "date" } as OptionType,
      { title: { value: "EMPTY", label: undefined }, type: "status" },
      { title: { value: "NULL", label: undefined }, type: "status" },
      { title: { value: "OR", label: undefined }, type: "operator" },
      { title: { value: "AND", label: undefined }, type: "operator" },
      { title: { value: "(", label: undefined }, type: "parenthesis" },
      { title: { value: ")", label: undefined }, type: "parenthesis" },
      {
        title: { value: "type your values in this format: (val1, val2, ...) and press enter", label: null },
        type: "inCondition",
      },
      {
        title: { value: "Asset type", label: undefined },
        type: "entity",
        values: Object.keys(ObjectType).map((x) => {
          return { value: x, label: null };
        }),
        tagName: "assetType",
      },
      {
        title: { value: "Asset class", label: undefined },
        type: "entity",
        values: Object.keys(ObjectClass).map((x) => {
          return { value: x, label: null };
        }),
        tagName: "assetClass",
      },
      {
        title: { value: "Asset status", label: undefined },
        type: "entity",
        values: Object.keys(ObjectStatus).map((x) => {
          return { value: x, label: null };
        }),
        tagName: "assetStatus",
      },
    ].concat(
      cacheService
        .GetMetadata()
        .filter((x) => x.isSearchable && x.tagName !== null)
        .map((x) => ({
          title: { value: x.label!, label: undefined },
          type: "entity",
          values: x.values
            ? x.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
              x.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
              x.baseType === BaseMetadataType.DbCastsMetadataValueInfos ||
              x.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos
              ? x.values
                  .map((y) => {
                    return { value: y.value, label: y.label };
                  })
                  .concat([{ value: "Type anything and press enter", label: null }])
              : x.values.map((y) => {
                  return { value: y.value, label: y.label };
                })
            : undefined,
          layer: x.layer ? ObjectLayer[x.layer] : undefined,
          tagName: x.tagName!,
          baseType: x.baseType ?? undefined,
        }))
        .sort(function (a, b) {
          if (a.title.value < b.title.value) {
            return -1;
          } else {
            return 1;
          }
        })
    )
  );

  const returnStringFromTitle = (title: { value?: string | null; label?: string | null }) => {
    return (title.label && title.label !== title.value ? title.value + " (" + title.label + ")" : title.value) ?? "";
  };

  const openModalMetadata = () => {
    if (layer === undefined) return;
    onChangeFocus(false);
    if (!openModal) return;
    openModal<ModalAdvancedQueryElementProps>(ModalSCreateAdvancedQuery, {
      query: convertAdvancedQueryToString(advancedQuery.slice(2)),
      queries: advancedQueries,
      addQuery: addAdvancedQuery,
      layer: layer,
      textSearch: textSearch,
    });
  };

  React.useEffect(() => {
    let tmp: OptionType[] = [];
    options.forEach((option) => {
      if (option.values) {
        option.values.map((value) => {
          tmp.push({
            title: value,
            type: "status",
            tagName: option.tagName,
          });
        });
      }
    });
    setOptions([...options, ...tmp]);
  }, []);

  React.useEffect(() => {
    if (props.textQuery && props.textQuery !== "") {
      setAdvancedQuery(convertStringToAdvancedQuery(props.textQuery, ObjectLayer.Asset));
    }
  }, [props.textQuery]);

  React.useEffect(() => {
    onQueryChange &&
      onQueryChange(
        advancedQuery && advancedQuery.length > 0 ? convertAdvancedQueryToString(advancedQuery.slice(2)) : ""
      );
  }, [advancedQuery, onQueryChange]);

  const onAutocompleteChange = (value: OptionType[]) => {
    if (value.length && value[0].type === "parenthesis") {
      setAdvancedQuery((prevState) => {
        const newState = [...prevState];
        newState.push({ parenthesis: returnStringFromTitle(value[0].title) });
        return newState;
      });
      return;
    }
    const isCompleteForSearchFor = autocompleteQueryType === "searchFor" && value.length === 1;
    const isComplete = (autocompleteQueryType === "operator" && value.length === 1) || value.length === 3;

    if (isCompleteForSearchFor) {
      setLayer(ObjectLayer[returnStringFromTitle(value[0].title)]);
    }
    if (value.length === 0 && autocompleteQueryType === "condition") {
      setWasEntity(true);
    }
    setAutocompleteValue(() => {
      if (isComplete || isCompleteForSearchFor) {
        return [];
      }
      return value;
    });

    if (isComplete) {
      setAdvancedQuery((prevState) => {
        const newState = [...prevState];
        if (autocompleteQueryType === "operator") {
          newState.push({ operator: returnStringFromTitle(value[0].title) });
        } else {
          newState.push({
            tagName: value[0].tagName!,
            start: returnStringFromTitle(value[0].title),
            condition: returnStringFromTitle(value[1].title),
            end: `"${value[2].title.value}"`,
            color: value[1].color || "success",
          });
        }
        return newState;
      });
    } else if (isCompleteForSearchFor) {
      setAutocompleteHasSearchFor(true);
      setAdvancedQuery((prevState) => {
        const newState = [...prevState];
        newState.push({
          start: "Type",
          condition: "IS",
          end: returnStringFromTitle(value[0].title),
          color: "success",
        });
        return newState;
      });
    }
  };

  function isEnclosedByParentheses(string) {
    // check for parentheses at the start and end of the string
    const pattern = /^\(.*\)$/;
    return pattern.test(string);
  }

  const onClickBox = React.useCallback(() => {
    if (!$autocompletRef.current) return;
    const input = $autocompletRef.current.querySelector("input");
    if (!input) return;
    onChangeFocus(true);
    input.focus();
    setAutocompleteOpen(true);
  }, [onChangeFocus]);

  const onInputBaseKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    wasEntityFilterEmpty: boolean
  ) => {
    setEntityFilter(e.currentTarget.value ? e.currentTarget.value : "");
    if (autocompleteQueryType === "status") setValuesFilter(e.currentTarget.value ? e.currentTarget.value : "");
    if (autocompleteQueryType === "searchFor") setSearchForFilter(e.currentTarget.value ? e.currentTarget.value : "");
    if (e.code.toLowerCase() === "backspace" && wasEntityFilterEmpty && autocompleteValue.length === 0 && !wasEntity) {
      setAdvancedQuery((prevState) => {
        const newState = [...prevState];
        newState.pop();
        return newState;
      });
    }
    if (
      e.code.toLocaleLowerCase() === "enter" &&
      ["IN", "'NOT-IN'"].includes(autocompleteValue[1].title.value as string)
    ) {
      let tmp: string = e.currentTarget.value;
      if (isEnclosedByParentheses(e.currentTarget.value))
        setAdvancedQuery((prevState) => {
          const newState = [...prevState];
          setAutocompleteValue([]);
          newState.push({
            tagName: autocompleteValue[0].tagName!,
            start: returnStringFromTitle(autocompleteValue[0].title),
            condition: returnStringFromTitle(autocompleteValue[1].title),
            end: tmp,
            color: autocompleteValue[1].color || "success",
          });
          return newState;
        });
    }
    if (
      e.code.toLocaleLowerCase() === "enter" &&
      autocompleteValue[0].baseType &&
      (autocompleteValue[0].baseType === BaseMetadataType.DbStringMetadataValueInfos ||
        autocompleteValue[0].baseType === BaseMetadataType.DbCastsMetadataValueInfos ||
        autocompleteValue[0].baseType === BaseMetadataType.DbSynopsesMetadataValueInfos ||
        autocompleteValue[0].baseType === BaseMetadataType.DbStringMultiMetadataValueInfos) &&
      e.currentTarget.value &&
      e.currentTarget.value.trim() !== ""
    ) {
      let tmp: string = e.currentTarget.value;
      setAdvancedQuery((prevState) => {
        const newState = [...prevState];
        setAutocompleteValue([]);
        newState.push({
          tagName: autocompleteValue[0].tagName!,
          start: returnStringFromTitle(autocompleteValue[0].title),
          condition: returnStringFromTitle(autocompleteValue[1].title),
          end: `"${tmp.trim()}"`,
          color: autocompleteValue[1].color || "success",
        });
        return newState;
      });
    }
    setWasEntity(false);
  };

  const onInputBaseBlur = () => {
    if (dateValid()) return;
    setAutocompleteOpen(false);
  };

  const onClickAway = React.useCallback(() => {
    onChangeFocus(false);
  }, [onChangeFocus]);

  const onClickReset: React.MouseEventHandler<HTMLButtonElement> = React.useCallback((e) => {
    if (e) {
      e.stopPropagation();
    }
    setAdvancedQuery([]);
    setAutocompleteValue([]);
  }, []);

  const dateValid = () => {
    return (
      autocompleteValue &&
      autocompleteValue[0] &&
      autocompleteValue[0].baseType === BaseMetadataType.DbDateTimeMetadataValueInfos &&
      autocompleteValue.length === 2
    );
  };

  React.useEffect(() => {
    if (!advancedQuery.length) {
      setAutocompleteHasSearchFor(false);
    }
    setAutocompleteQueryType(() => {
      if (autocompleteValue.length === 1) return "condition";
      if (
        autocompleteValue.length === 2 &&
        autocompleteValue[1].title.value !== "IN" &&
        autocompleteValue[1].title.value !== "'NOT-IN'"
      ) {
        return "status";
      }
      if (
        autocompleteValue.length === 2 &&
        (autocompleteValue[1].title.value === "IN" || autocompleteValue[1].title.value === "'NOT-IN'")
      ) {
        return "inCondition";
      }

      if (entityFilter !== "") setEntityFilter("");
      if (valuesFilter !== "") setValuesFilter("");

      if (advancedQuery[advancedQuery.length - 1] && "parenthesis" in advancedQuery[advancedQuery.length - 1])
        if ((advancedQuery[advancedQuery.length - 1] as { parenthesis: string }).parenthesis === ")") return "operator";

      if (advancedQuery.length === 0) return "searchFor";
      if (advancedQuery.length === 1 || advancedQuery[advancedQuery.length - 1].hasOwnProperty("start"))
        return "operator";
      return "entity";
    });
  }, [advancedQuery, autocompleteValue]);

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Stack spacing={1.5} direction="row" alignItems="center" flex={1} {...otherProps}>
        <Box flex={1} {...otherProps}>
          <StyledBox
            onClick={onClickBox}
            isValid={
              advancedQuery.length > 2
              //   && advancedQuery[advancedQuery.length - 1].hasOwnProperty("tagName") === true
            }
          >
            <StyledBoxWrapper>
              {!autocompleteHasSearchFor && (
                <Typography mb={0.5}>{t("component:advancedQuery.body.searchFor")}</Typography>
              )}
              {advancedQuery.length > 0 && <AdvancedQueries queries={advancedQuery} withWrapper={false} />}
              <Autocomplete
                multiple
                open={autocompleteOpen}
                id="multiple-limit-tags"
                options={options}
                getOptionLabel={(option) => returnStringFromTitle(option.title)}
                isOptionEqualToValue={(option, value) => option.title === value.title}
                popupIcon={<></>}
                ref={$autocompletRef}
                onBlur={onInputBaseBlur}
                noOptionsText={
                  autocompleteValue.length &&
                  (autocompleteValue[0].baseType === BaseMetadataType.DbStringMetadataValueInfos ||
                    autocompleteValue[0].baseType === BaseMetadataType.DbStringMultiMetadataValueInfos)
                    ? t("field:helper.pressEnter")
                    : t("field:helper.noOptions")
                }
                renderOption={(props, option) => {
                  if (dateValid() && returnStringFromTitle(option.title) !== "NULL") {
                    return (
                      <li {...props} onClick={(e) => true} key={returnStringFromTitle(option.title) + option.layer}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            value={undefined}
                            label={t("field:label.selectDate")}
                            onChange={(newValue) => {
                              onAutocompleteChange([
                                autocompleteValue[0],
                                autocompleteValue[1],
                                {
                                  title: {
                                    value: moment(newValue?.toString()).format(
                                      GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS
                                    ),
                                    label: undefined,
                                  },
                                  type: "status",
                                },
                              ]);
                            }}
                            format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}

                            //renderInput={(params) => <TextField sx={{}} {...params} />}
                          />
                        </LocalizationProvider>
                      </li>
                    );
                  } else if (
                    (returnStringFromTitle(option.title) !== "NULL" && !autocompleteValue) ||
                    !autocompleteValue[0] ||
                    autocompleteValue[0].baseType !== BaseMetadataType.DbDateTimeMetadataValueInfos ||
                    autocompleteValue.length !== 2
                  )
                    return (
                      <li {...props} key={returnStringFromTitle(option.title) + option.layer}>
                        {returnStringFromTitle(option.title)}
                        {option.layer && <Chip label={option.layer} sx={{ ml: 1 }} size="small" />}
                      </li>
                    );
                }}
                renderInput={(params) => {
                  return (
                    <InputBase
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      onKeyUp={(e) => onInputBaseKeyDown(e, entityFilter === "")}
                    />
                  );
                }}
                renderTags={(tags) =>
                  tags.length < 3 ? (
                    <>
                      <Typography>{tags.map((tag) => `${returnStringFromTitle(tag.title)} `)}</Typography>
                    </>
                  ) : (
                    <></>
                  )
                }
                getOptionDisabled={(option) =>
                  returnStringFromTitle(option.title).toLowerCase() === "type anything and press enter" ||
                  returnStringFromTitle(option.title).toLowerCase() ===
                    "type your values in this format: (val1, val2, ...) and press enter"
                }
                filterOptions={
                  advancedQuery.length === 1
                    ? (options) => {
                        return options.filter((x) => returnStringFromTitle(x.title) === "AND");
                      }
                    : (options) => {
                        return options
                          .filter(
                            (option) =>
                              (autocompleteQueryType === "searchFor" &&
                                option.type === "searchFor" &&
                                returnStringFromTitle(option.title).toLowerCase().includes(searchForFilter)) ||
                              (autocompleteQueryType === "condition" &&
                                option.type === "condition" &&
                                (autocompleteValue.length &&
                                autocompleteValue[0].baseType === BaseMetadataType.DbDateTimeMetadataValueInfos
                                  ? option.tagName === "date"
                                  : option.tagName !== "date")) ||
                              (autocompleteQueryType === "operator" &&
                                (option.type === "parenthesis" || option.type === "operator")) ||
                              (autocompleteQueryType === "entity" &&
                                (option.type === "parenthesis" ||
                                  (option.type === "entity" &&
                                    returnStringFromTitle(option.title)
                                      .toLowerCase()
                                      .includes(entityFilter.toLowerCase())))) ||
                              (autocompleteQueryType === "status" &&
                                option.type === "status" &&
                                ((autocompleteValue.length > 0 && option.tagName === autocompleteValue[0].tagName) ||
                                  option.tagName === undefined) &&
                                returnStringFromTitle(option.title)
                                  .toLowerCase()
                                  .includes(valuesFilter.trim().toLowerCase())) ||
                              (autocompleteQueryType === "inCondition" && option.type === "inCondition")
                          )
                          .sort((a, b) => {
                            if (a.layer === undefined || b.layer === undefined) return 1;
                            if (a.layer === layer) return -1;
                            if (b.layer === layer) return 1;
                            return a.layer?.toString().localeCompare(b.layer?.toString() || "") || -1;
                          })
                          .sort((a, b) => {
                            if (a.layer === b.layer)
                              return returnStringFromTitle(a.title).localeCompare(returnStringFromTitle(b.title));
                            else return 0;
                          })
                          .sort((a, b) => {
                            if (returnStringFromTitle(a.title).toLowerCase() === "type anything and press enter")
                              return -1;
                            else return 0;
                          });
                      }
                }
                size="large"
                value={autocompleteValue}
                onKeyDown={(event: any) => {
                  if (event.key !== "Backspace" && dateValid()) {
                    event.stopPropagation();
                  }
                }}
                onChange={(e, value) => {
                  onAutocompleteChange(value);
                }}
                openOnFocus
              />
            </StyledBoxWrapper>
            <IconButton onClick={onClickReset} disabled={advancedQuery.length < 1}>
              <CloseIcon sx={{ color: "text.tier" }} />
            </IconButton>
          </StyledBox>
        </Box>
        {!hideButtons && (
          <Button
            color="primary"
            variant="contained"
            size="large"
            disabled={!textSearch.length || advancedQuery.length < 3}
            onClick={() => onClickSearch(convertAdvancedQueryToString(advancedQuery.slice(2)), layer!, "")}
            startIcon={<SearchIcon />}
          >
            {t("common:search")}
          </Button>
        )}
        {!hideButtons && (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            disabled={advancedQuery.length < 1}
            onClick={openModalMetadata}
          >
            {t("button:saveQuery")}
          </Button>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export default AdvancedQueryBuilder;

const StyledBox = styled(Box, { shouldForwardProp: (prop) => prop !== "isValid" })<{
  isValid: boolean;
}>(({ theme, isValid }) => ({
  borderRadius: 6,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 12,
  paddingRight: 12,
  minHeight: 48,
  display: "flex",
  alignItems: "center",
  width: "100%",
  background: theme.palette.backgroundAlpha.main,
  borderBottom: isValid ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.error.main}`,

  ".MuiChip-root": {
    "&:hover": {
      background: "transparent",
      borderColor: theme.palette.backgroundAlpha.border,
    },
  },

  ".MuiAutocomplete-root": {
    flex: 1,
    marginBottom: 0,

    ".MuiAutocomplete-endAdornment": {
      display: "none",
    },

    ".MuiInputBase-root": {
      padding: 0,
      paddingLeft: theme.spacing(1),
      minWidth: 250,
      minHeight: 24,
    },
  },
}));

const StyledBoxWrapper = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  flexWrap: "wrap",
  flex: 1,
}));
